'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { CustomHeader } from './custom-header';
import type { ImageType } from '@eliadmin/core/src/api/api-types';

export type Menu = {
  name: string;
  main_type: 'footer' | 'header' | 'topbar';
  fields: MenuField[];
};

export type MenuField = {
  type: 'page';
  name: string;
  /** without / prefix */
  image?: ImageType;
  path?: string;
  href?: string;
  subpages?: MenuField[];
  popover?: boolean;
};

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left text-xl font-semibold',
              layer === 1 && 'text-[16px] text-lg font-medium',
              layer && layer > 1 && 'text-sm font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: { type: 'page'; name: string; path?: string }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  popoverMenus?: Menu[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);

  const categories = props.categories;

  const additionalMenuItem2 = { type: 'page', name: 'Kalendár', path: '/kalendar' };

  const additionalMenuItems = props.popoverMenus?.map((menuData, index) => ({
    type: 'page',
    name: menuData.name,
    path: `/${index === 0 ? 'sutaze' : index === 1 ? 'reprezentacia' : 'federacia'}`,
    subpages: menuData.fields?.map((field: MenuField, subIndex: number) => ({
      type: 'page',
      name: field.name,
      href: field.path,
      id: subIndex + 1,
    })),
    popover: true,
  }));

  // const menuItems = [...props.menu?.flat(), additionalMenuItems?.flat()]

  // console.log(menuItems);

  return (
    <>
      <Sheet>
        <SheetTrigger
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <Image
            alt="menu"
            width={0}
            height={0}
            sizes="100vw"
            className="size-7"
            src={'/images/mobile-menu.svg'}
          />
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={props.side}
          className={cn(
            'w-full overflow-scroll p-0',
            'ea-mobile-sidebar__content',

            props.className?.content,
          )}
          closeIcon={{ wrapper: 'right-4 top-4', icon: 'text-primary h-6 w-6 font-bold' }}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          {generalLayout.header?.logo ? (
            <Image
              src={generalLayout.header?.logo?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(generalLayout?.header?.logo, undefined)}
              width={0}
              height={0}
              sizes="100vw"
              className={cn(
                ' w-full min-w-[135px] max-w-[200px] px-6 pt-6',
                'ea-header-logo-mobile-sidebar__image',
              )}
            />
          ) : (
            <div className={cn('text-2xl font-bold', 'ea-header-logo__text')}>
              {(generalLayout.site_name?.trim() || '')}
            </div>
          )}
          <Separator className="my-5 w-full" />
          <div className="px-3">
            <ul className={cn('overflow-scroll', 'ea-menu-sidebar__list')}>
              <Accordion type="single" collapsible>
                {categories?.map((category, index) => (
                  <CategoryItem key={index} category={category} />
                ))}
              </Accordion>
            </ul>
            {!!props.menu && (
              //menu items 1 and 2
              <div className="flex flex-col gap-2 p-4">
                {/* Display the first menu item */}
                {props.menu[0] && (
                  <SheetClose key={0} asChild>
                    <Link prefetchOnHover href={props.menu[0].path!} className="flex w-full" prefetch={false}>
                      <div className={cn('text-[16px]', '')}>{props.menu[0].name}</div>
                    </Link>
                  </SheetClose>
                )}

                {!!additionalMenuItems && (
                  <>
                    {additionalMenuItems.map((menuItem, index) => (
                      <Accordion
                        className={cn('ea-mobile-sidebar__accordion')}
                        key={index}
                        type="single"
                        collapsible
                      >
                        <AccordionItem
                          className={cn('border-t-0', 'ea-mobile-sidebar__accordion-item')}
                          value={menuItem.name}
                        >
                          <AccordionTrigger
                            className={cn(
                              'border-t-0 py-0 hover:font-semibold hover:text-primary hover:no-underline',
                              'ea-mobile-sidebar__accordion-trigger',
                            )}
                          >
                            <h2 className={cn('text-[16px]', '')}>{menuItem.name}</h2>
                          </AccordionTrigger>
                          <AccordionContent className="ml-5 mt-2 flex flex-col gap-2">
                            {menuItem.subpages.map((subpage, subIndex) => (
                              <SheetClose key={subIndex} asChild>
                                <Link prefetchOnHover href={subpage.href!} className="flex w-full" prefetch={false}>
                                  <h2 className={cn('text-[16px]', '')}>{subpage.name}</h2>
                                </Link>
                              </SheetClose>
                            ))}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ))}
                  </>
                )}

                {/* Display the second menu item */}
                {props.menu[1] && (
                  <SheetClose key={1} asChild>
                    <Link prefetchOnHover href={props.menu[1].path!} className="flex w-full" prefetch={false}>
                      <div className={cn('text-[16px]', '')}>{props.menu[1].name}</div>
                    </Link>
                  </SheetClose>
                )}
                {additionalMenuItem2 && (
                  <SheetClose asChild>
                    <Link prefetchOnHover href={additionalMenuItem2.path!} className="flex w-full" prefetch={false}>
                      <div className={cn('text-[16px]', '')}>{additionalMenuItem2.name}</div>
                    </Link>
                  </SheetClose>
                )}
              </div>
            )}

            <div className="p-4">
              <Separator className="mb-10" />
              <CustomHeader showText={true} />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
